import jsPDF from 'jspdf';
import 'jspdf-autotable';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';

// Format currency helper
const formatCurrency = (value, currency = 'USD') => {
    return new Intl.NumberFormat(currency === 'USD' ? 'en-US' : 'tr-TR', {
        style: 'currency',
        currency: currency === 'USD' ? 'USD' : 'TRY'
    }).format(value);
};

// PDF Export with jsPDF
export const exportToPDF = (data, stats, lang = 'tr') => {
    if (!data || data.length === 0) return;

    const doc = new jsPDF();
    const dateStr = new Date().toLocaleDateString(lang === 'tr' ? 'tr-TR' : 'en-US');

    // Title
    doc.setFontSize(22);
    doc.setTextColor(0, 212, 255);
    doc.text(lang === 'tr' ? 'Getiri Hesaplama Raporu' : 'Profit Calculation Report', 14, 20);

    // Date
    doc.setFontSize(10);
    doc.setTextColor(100);
    doc.text((lang === 'tr' ? 'Tarih: ' : 'Date: ') + dateStr, 14, 28);

    // Summary Section
    doc.setFontSize(14);
    doc.setTextColor(0);
    doc.text(lang === 'tr' ? 'Ozet Bilgiler' : 'Summary', 14, 42);

    const summaryData = [
        [lang === 'tr' ? 'Baslangic Bakiye' : 'Initial Balance', formatCurrency(stats.initialBalance || 0)],
        [lang === 'tr' ? 'Son Bakiye' : 'Final Balance', formatCurrency(stats.finalBalance || 0)],
        [lang === 'tr' ? 'Toplam Kar' : 'Total Profit', formatCurrency(stats.totalProfit || 0)],
        [lang === 'tr' ? 'Gunluk Ortalama' : 'Daily Average', formatCurrency(stats.dailyAverage || 0)],
        [lang === 'tr' ? 'Kar Orani' : 'Profit Rate', ((stats.profitRate || 0).toFixed(2)) + '%'],
    ];

    doc.autoTable({
        startY: 46,
        head: [[lang === 'tr' ? 'Parametre' : 'Parameter', lang === 'tr' ? 'Deger' : 'Value']],
        body: summaryData,
        theme: 'striped',
        headStyles: { fillColor: [0, 212, 255], textColor: [255, 255, 255] },
        styles: { fontSize: 10 },
        columnStyles: { 0: { fontStyle: 'bold' } }
    });

    // Daily Results Table
    const tableY = doc.lastAutoTable.finalY + 12;
    doc.setFontSize(14);
    doc.text(lang === 'tr' ? 'Gunluk Detaylar' : 'Daily Details', 14, tableY);

    const tableData = data.map(row => [
        row.day + '. ' + (lang === 'tr' ? 'Gun' : 'Day'),
        formatCurrency(row.usdt || row.balance || 0),
        formatCurrency(row.try || 0, 'TRY'),
        '+' + formatCurrency(row.profit || 0),
    ]);

    doc.autoTable({
        startY: tableY + 4,
        head: [[
            lang === 'tr' ? 'Gun' : 'Day',
            lang === 'tr' ? 'Bakiye (USDT)' : 'Balance (USDT)',
            lang === 'tr' ? 'TL Karsiligi' : 'TRY Equivalent',
            lang === 'tr' ? 'Gunluk Kar' : 'Daily Profit',
        ]],
        body: tableData,
        theme: 'grid',
        headStyles: { fillColor: [0, 255, 136], textColor: [0, 0, 0] },
        styles: { fontSize: 9 },
        alternateRowStyles: { fillColor: [245, 245, 245] }
    });

    // Footer
    const pageCount = doc.internal.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
        doc.setPage(i);
        doc.setFontSize(8);
        doc.setTextColor(150);
        doc.text(
            'Getiri Hesaplama - ' + (lang === 'tr' ? 'Sayfa' : 'Page') + ' ' + i + '/' + pageCount,
            doc.internal.pageSize.getWidth() / 2,
            doc.internal.pageSize.getHeight() - 10,
            { align: 'center' }
        );
    }

    const filename = 'getiri_rapor_' + new Date().toISOString().split('T')[0] + '.pdf';
    doc.save(filename);
    return filename;
};

// Excel Export with XLSX
export const exportToExcel = (data, stats, lang = 'tr') => {
    if (!data || data.length === 0) return;

    const workbook = XLSX.utils.book_new();

    // Summary Sheet
    const summaryData = [
        [lang === 'tr' ? 'Getiri Hesaplama Raporu' : 'Profit Calculation Report'],
        [],
        [lang === 'tr' ? 'Tarih' : 'Date', new Date().toLocaleDateString(lang === 'tr' ? 'tr-TR' : 'en-US')],
        [],
        [lang === 'tr' ? 'Parametre' : 'Parameter', lang === 'tr' ? 'Deger' : 'Value'],
        [lang === 'tr' ? 'Baslangic Bakiye' : 'Initial Balance', stats.initialBalance || 0],
        [lang === 'tr' ? 'Son Bakiye' : 'Final Balance', stats.finalBalance || 0],
        [lang === 'tr' ? 'Toplam Kar' : 'Total Profit', stats.totalProfit || 0],
        [lang === 'tr' ? 'Gunluk Ortalama' : 'Daily Average', stats.dailyAverage || 0],
        [lang === 'tr' ? 'Kar Orani (%)' : 'Profit Rate (%)', stats.profitRate || 0],
    ];

    const summarySheet = XLSX.utils.aoa_to_sheet(summaryData);
    summarySheet['!cols'] = [{ wch: 25 }, { wch: 20 }];
    XLSX.utils.book_append_sheet(workbook, summarySheet, lang === 'tr' ? 'Ozet' : 'Summary');

    // Daily Details Sheet
    const detailsHeader = [
        lang === 'tr' ? 'Gun' : 'Day',
        lang === 'tr' ? 'Bakiye (USDT)' : 'Balance (USDT)',
        lang === 'tr' ? 'TL Karsiligi' : 'TRY Equivalent',
        lang === 'tr' ? 'Gunluk Kar' : 'Daily Profit',
    ];

    const detailsData = [
        detailsHeader,
        ...data.map(row => [
            row.day,
            row.usdt || row.balance || 0,
            row.try || 0,
            row.profit || 0,
        ])
    ];

    const detailsSheet = XLSX.utils.aoa_to_sheet(detailsData);
    detailsSheet['!cols'] = [{ wch: 10 }, { wch: 18 }, { wch: 18 }, { wch: 15 }];
    XLSX.utils.book_append_sheet(workbook, detailsSheet, lang === 'tr' ? 'Gunluk Detay' : 'Daily Details');

    // Save file
    const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
    const blob = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    const filename = 'getiri_rapor_' + new Date().toISOString().split('T')[0] + '.xlsx';
    saveAs(blob, filename);
    return filename;
};

// CSV Export
export const exportToCSV = (data, lang = 'tr') => {
    if (!data || data.length === 0) return;

    const headers = [
        lang === 'tr' ? 'Gun' : 'Day',
        lang === 'tr' ? 'Bakiye (USDT)' : 'Balance (USDT)',
        lang === 'tr' ? 'TL Karsiligi' : 'TRY Equivalent',
        lang === 'tr' ? 'Gunluk Kar' : 'Daily Profit',
    ];

    const csvContent = [
        headers.join(','),
        ...data.map(row => [
            row.day,
            (row.usdt || row.balance || 0).toFixed(2),
            (row.try || 0).toFixed(2),
            (row.profit || 0).toFixed(2)
        ].join(','))
    ].join('\n');

    const BOM = '\uFEFF';
    const blob = new Blob([BOM + csvContent], { type: 'text/csv;charset=utf-8;' });
    const filename = 'getiri_rapor_' + new Date().toISOString().split('T')[0] + '.csv';
    saveAs(blob, filename);
    return filename;
};

// JSON Export
export const exportToJSON = (data, stats, lang = 'tr') => {
    const exportData = {
        generatedAt: new Date().toISOString(),
        language: lang,
        summary: stats,
        dailyResults: data
    };

    const jsonContent = JSON.stringify(exportData, null, 2);
    const blob = new Blob([jsonContent], { type: 'application/json' });
    const filename = 'getiri_rapor_' + new Date().toISOString().split('T')[0] + '.json';
    saveAs(blob, filename);
    return filename;
};
